import { useTranslation } from 'next-i18next'
import React, { ChangeEvent, FC, InputHTMLAttributes, useCallback, useEffect, useState } from 'react'
import { batch } from 'react-redux'
import { useDebounce, useWindowSize } from 'usehooks-ts'

import {
  pressReleaseSetIsEventFilter,
  pressReleaseSetSelected,
  pressReleaseSetSkip,
  pressReleaseSetTitle,
} from '@/reducers/press-release'
import clsxm from '@/utils/clsxm'
import { useAppDispatch, useAppSelector } from '@/utils/hooks'

import IcDismiss from '~/assets/icons/icon-dismiss.svg'
import IcSearch from '~/assets/icons/icon-search.svg'

type SearchBarProps = InputHTMLAttributes<HTMLInputElement>

const SearchBar: FC<SearchBarProps> = ({ ...rest }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [title, setTitle] = useState('')
  const { width } = useWindowSize()
  const titleDebounce = useDebounce(title, width <= 768 ? 1000 : 500)
  const pressReleaseTitle = useAppSelector((state) => state.pressRelease.title)
  const handleChangeSearch = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setTitle(value)
  }

  const reset = () => {
    batch(() => {
      dispatch(pressReleaseSetIsEventFilter(true))
      dispatch(pressReleaseSetSkip(0))
      dispatch(pressReleaseSetSelected(0))
    })
  }

  const handleResetSearch = useCallback(() => {
    dispatch(pressReleaseSetTitle(''))
    reset()
  }, [])

  useEffect(() => {
    dispatch(pressReleaseSetTitle(titleDebounce))
    reset()
  }, [titleDebounce])

  useEffect(() => {
    setTitle(pressReleaseTitle)
  }, [pressReleaseTitle])

  return (
    <div className='relative w-full'>
      <input
        placeholder={String(t('press:placeholder'))}
        className={clsxm(
          'w-full rounded-lg border-[0.5px] border-neutral-border bg-white px-4 py-2 pr-12 lg:px-5 lg:py-4',
          'flex-1 border-0 text-dark',
          'placeholder:text-sm placeholder:font-medium placeholder:text-neutral-placeholder focus:outline-0 focus:ring-0',
          rest.className
        )}
        onChange={handleChangeSearch}
        value={title}
      />

      <button
        className={clsxm(
          'absolute inset-y-0 right-0 flex cursor-pointer items-center justify-center pr-[10px] lg:pr-5'
        )}
        onClick={pressReleaseTitle.length > 0 ? handleResetSearch : () => null}
      >
        {pressReleaseTitle.length > 0 ? (
          <IcDismiss className={clsxm('block h-6 w-6')} />
        ) : (
          <IcSearch className={clsxm('block h-6 w-6')} />
        )}
      </button>
    </div>
  )
}

export default SearchBar
